/**
 * Maximum-weight matching in general graphs (Edmonds' blossom algorithm with
 * Galil's O(n^3) primal-dual bookkeeping).
 *
 * The structure follows the classic description in Z. Galil, "Efficient
 * algorithms for finding maximum matching in graphs" (ACM Computing Surveys,
 * 1986) and the well-known public-domain reference implementation by Joris van
 * Rantwijk (mwmatching.py, 2008). Weights are BigInt so that pairing engines
 * can encode many lexicographic criteria into a single edge weight without
 * losing precision. All arithmetic stays integral: with integer weights the
 * slack of an edge between two S-blossoms is always even.
 */

export interface WeightedEdge {
  readonly u: number;
  readonly v: number;
  readonly w: bigint;
}

export interface MatchingOptions {
  /**
   * When true, return a maximum-weight matching among the matchings of
   * maximum cardinality (every vertex that can be matched is matched).
   */
  readonly maxCardinality?: boolean;
}

/** Modular index into an array, like Python's negative indices. */
function at<T>(arr: readonly T[], idx: number): T {
  const n = arr.length;
  return arr[((idx % n) + n) % n] as T;
}

/**
 * Compute a maximum-weight matching.
 *
 * @param n number of vertices (0..n-1)
 * @param edges undirected edges; no self-loops, at most one edge per pair
 * @returns mate array: mate[v] is v's partner, or -1 when v is unmatched
 */
export function maxWeightMatching(
  n: number,
  edges: readonly WeightedEdge[],
  options: MatchingOptions = {},
): Int32Array {
  const maxCardinality = options.maxCardinality ?? false;
  const result = new Int32Array(n).fill(-1);
  if (edges.length === 0 || n === 0) return result;

  const nedge = edges.length;
  const nvertex = n;

  let maxweight = 0n;
  for (const e of edges) {
    if (e.u === e.v) throw new Error('maxWeightMatching: self-loop');
    if (e.u < 0 || e.u >= n || e.v < 0 || e.v >= n) {
      throw new Error('maxWeightMatching: vertex out of range');
    }
    if (e.w > maxweight) maxweight = e.w;
  }

  // endpoint[p] is the vertex of endpoint p; edge k has endpoints 2k and 2k+1.
  const endpoint = new Int32Array(2 * nedge);
  const ew: bigint[] = new Array<bigint>(nedge);
  const eu = new Int32Array(nedge);
  const evv = new Int32Array(nedge);
  for (let k = 0; k < nedge; k++) {
    const e = edges[k] as WeightedEdge;
    endpoint[2 * k] = e.u;
    endpoint[2 * k + 1] = e.v;
    eu[k] = e.u;
    evv[k] = e.v;
    ew[k] = e.w;
  }

  // neighbend[v]: remote endpoints of the edges incident to v.
  const neighbend: number[][] = Array.from({ length: nvertex }, () => []);
  for (let k = 0; k < nedge; k++) {
    (neighbend[eu[k] as number] as number[]).push(2 * k + 1);
    (neighbend[evv[k] as number] as number[]).push(2 * k);
  }

  // mate[v]: remote endpoint of v's matched edge, or -1.
  const mate = new Int32Array(nvertex).fill(-1);
  // label[b]: 0 free, 1 S, 2 T (for top-level blossoms and vertices); bit 4 temporary.
  const label = new Int32Array(2 * nvertex);
  const labelend = new Int32Array(2 * nvertex).fill(-1);
  const inblossom = new Int32Array(nvertex);
  for (let v = 0; v < nvertex; v++) inblossom[v] = v;
  const blossomparent = new Int32Array(2 * nvertex).fill(-1);
  const blossomchilds: (number[] | null)[] = new Array(2 * nvertex).fill(null);
  const blossombase = new Int32Array(2 * nvertex).fill(-1);
  for (let v = 0; v < nvertex; v++) blossombase[v] = v;
  const blossomendps: (number[] | null)[] = new Array(2 * nvertex).fill(null);
  const bestedge = new Int32Array(2 * nvertex).fill(-1);
  const blossombestedges: (number[] | null)[] = new Array(2 * nvertex).fill(null);
  const unusedblossoms: number[] = [];
  for (let b = nvertex; b < 2 * nvertex; b++) unusedblossoms.push(b);
  const dualvar: bigint[] = new Array<bigint>(2 * nvertex);
  for (let v = 0; v < nvertex; v++) dualvar[v] = maxweight;
  for (let b = nvertex; b < 2 * nvertex; b++) dualvar[b] = 0n;
  const allowedge = new Uint8Array(nedge);
  let queue: number[] = [];

  const slack = (k: number): bigint =>
    (dualvar[eu[k] as number] as bigint) +
    (dualvar[evv[k] as number] as bigint) -
    2n * (ew[k] as bigint);

  const blossomLeaves = (b: number, out: number[] = []): number[] => {
    if (b < nvertex) {
      out.push(b);
    } else {
      for (const t of blossomchilds[b] as number[]) {
        if (t < nvertex) out.push(t);
        else blossomLeaves(t, out);
      }
    }
    return out;
  };

  const assignLabel = (w: number, t: number, p: number): void => {
    const b = inblossom[w] as number;
    label[w] = t;
    label[b] = t;
    labelend[w] = p;
    labelend[b] = p;
    bestedge[w] = -1;
    bestedge[b] = -1;
    if (t === 1) {
      blossomLeaves(b, queue);
    } else if (t === 2) {
      const base = blossombase[b] as number;
      const mb = mate[base] as number;
      assignLabel(endpoint[mb] as number, 1, mb ^ 1);
    }
  };

  const scanBlossom = (v0: number, w0: number): number => {
    let v = v0;
    let w = w0;
    const path: number[] = [];
    let base = -1;
    while (v !== -1 || w !== -1) {
      let b = inblossom[v] as number;
      if ((label[b] as number) & 4) {
        base = blossombase[b] as number;
        break;
      }
      path.push(b);
      label[b] = 5;
      if (labelend[b] === -1) {
        v = -1;
      } else {
        v = endpoint[labelend[b] as number] as number;
        b = inblossom[v] as number;
        v = endpoint[labelend[b] as number] as number;
      }
      if (w !== -1) {
        const tmp = v;
        v = w;
        w = tmp;
      }
    }
    for (const b of path) label[b] = 1;
    return base;
  };

  const addBlossom = (base: number, k: number): void => {
    let v = eu[k] as number;
    let w = evv[k] as number;
    const bb = inblossom[base] as number;
    let bv = inblossom[v] as number;
    let bw = inblossom[w] as number;
    const b = unusedblossoms.pop() as number;
    blossombase[b] = base;
    blossomparent[b] = -1;
    blossomparent[bb] = b;
    let path: number[] = [];
    let endps: number[] = [];
    while (bv !== bb) {
      blossomparent[bv] = b;
      path.push(bv);
      endps.push(labelend[bv] as number);
      v = endpoint[labelend[bv] as number] as number;
      bv = inblossom[v] as number;
    }
    path.push(bb);
    path = path.reverse();
    endps = endps.reverse();
    endps.push(2 * k);
    while (bw !== bb) {
      blossomparent[bw] = b;
      path.push(bw);
      endps.push((labelend[bw] as number) ^ 1);
      w = endpoint[labelend[bw] as number] as number;
      bw = inblossom[w] as number;
    }
    blossomchilds[b] = path;
    blossomendps[b] = endps;
    label[b] = 1;
    labelend[b] = labelend[bb] as number;
    dualvar[b] = 0n;
    for (const leaf of blossomLeaves(b)) {
      if (label[inblossom[leaf] as number] === 2) queue.push(leaf);
      inblossom[leaf] = b;
    }
    const bestedgeto = new Int32Array(2 * nvertex).fill(-1);
    for (const sub of path) {
      let nblists: number[][];
      const sbe = blossombestedges[sub];
      if (sbe === null || sbe === undefined) {
        nblists = blossomLeaves(sub).map((leaf) =>
          (neighbend[leaf] as number[]).map((p) => p >> 1),
        );
      } else {
        nblists = [sbe];
      }
      for (const nblist of nblists) {
        for (const kk of nblist) {
          let j = evv[kk] as number;
          if (inblossom[j] === b) j = eu[kk] as number;
          const bj = inblossom[j] as number;
          if (
            bj !== b &&
            label[bj] === 1 &&
            (bestedgeto[bj] === -1 || slack(kk) < slack(bestedgeto[bj] as number))
          ) {
            bestedgeto[bj] = kk;
          }
        }
      }
      blossombestedges[sub] = null;
      bestedge[sub] = -1;
    }
    const be: number[] = [];
    for (const kk of bestedgeto) if (kk !== -1) be.push(kk);
    blossombestedges[b] = be;
    bestedge[b] = -1;
    for (const kk of be) {
      if (bestedge[b] === -1 || slack(kk) < slack(bestedge[b] as number)) bestedge[b] = kk;
    }
  };

  const expandBlossom = (b: number, endstage: boolean): void => {
    const childs = blossomchilds[b] as number[];
    for (const s of childs) {
      blossomparent[s] = -1;
      if (s < nvertex) {
        inblossom[s] = s;
      } else if (endstage && dualvar[s] === 0n) {
        expandBlossom(s, endstage);
      } else {
        for (const leaf of blossomLeaves(s)) inblossom[leaf] = s;
      }
    }
    if (!endstage && label[b] === 2) {
      const endps = blossomendps[b] as number[];
      const entrychild = inblossom[endpoint[(labelend[b] as number) ^ 1] as number] as number;
      let j = childs.indexOf(entrychild);
      let jstep: number;
      let endptrick: number;
      if (j & 1) {
        j -= childs.length;
        jstep = 1;
        endptrick = 0;
      } else {
        jstep = -1;
        endptrick = 1;
      }
      let p = labelend[b] as number;
      while (j !== 0) {
        label[endpoint[p ^ 1] as number] = 0;
        label[endpoint[at(endps, j - endptrick) ^ endptrick ^ 1] as number] = 0;
        assignLabel(endpoint[p ^ 1] as number, 2, p);
        allowedge[at(endps, j - endptrick) >> 1] = 1;
        j += jstep;
        p = at(endps, j - endptrick) ^ endptrick;
        allowedge[p >> 1] = 1;
        j += jstep;
      }
      const bv0 = at(childs, j);
      label[endpoint[p ^ 1] as number] = 2;
      label[bv0] = 2;
      labelend[endpoint[p ^ 1] as number] = p;
      labelend[bv0] = p;
      bestedge[bv0] = -1;
      j += jstep;
      while (at(childs, j) !== entrychild) {
        const bv = at(childs, j);
        if (label[bv] === 1) {
          j += jstep;
          continue;
        }
        let found = -1;
        for (const leaf of blossomLeaves(bv)) {
          if (label[leaf] !== 0) {
            found = leaf;
            break;
          }
        }
        if (found !== -1) {
          label[found] = 0;
          label[endpoint[mate[blossombase[bv] as number] as number] as number] = 0;
          assignLabel(found, 2, labelend[found] as number);
        }
        j += jstep;
      }
    }
    label[b] = -1;
    labelend[b] = -1;
    blossomchilds[b] = null;
    blossomendps[b] = null;
    blossombase[b] = -1;
    blossombestedges[b] = null;
    bestedge[b] = -1;
    unusedblossoms.push(b);
  };

  const augmentBlossom = (b: number, v: number): void => {
    let t = v;
    while (blossomparent[t] !== b) t = blossomparent[t] as number;
    if (t >= nvertex) augmentBlossom(t, v);
    const childs = blossomchilds[b] as number[];
    const endps = blossomendps[b] as number[];
    const i = childs.indexOf(t);
    let j = i;
    let jstep: number;
    let endptrick: number;
    if (i & 1) {
      j -= childs.length;
      jstep = 1;
      endptrick = 0;
    } else {
      jstep = -1;
      endptrick = 1;
    }
    while (j !== 0) {
      j += jstep;
      t = at(childs, j);
      const p = at(endps, j - endptrick) ^ endptrick;
      if (t >= nvertex) augmentBlossom(t, endpoint[p] as number);
      j += jstep;
      t = at(childs, j);
      if (t >= nvertex) augmentBlossom(t, endpoint[p ^ 1] as number);
      mate[endpoint[p] as number] = p ^ 1;
      mate[endpoint[p ^ 1] as number] = p;
    }
    blossomchilds[b] = childs.slice(i).concat(childs.slice(0, i));
    blossomendps[b] = endps.slice(i).concat(endps.slice(0, i));
    blossombase[b] = blossombase[(blossomchilds[b] as number[])[0] as number] as number;
  };

  const augmentMatching = (k: number): void => {
    const pairs: [number, number][] = [
      [eu[k] as number, 2 * k + 1],
      [evv[k] as number, 2 * k],
    ];
    for (const [s0, p0] of pairs) {
      let s = s0;
      let p = p0;
      for (;;) {
        const bs = inblossom[s] as number;
        if (bs >= nvertex) augmentBlossom(bs, s);
        mate[s] = p;
        if (labelend[bs] === -1) break;
        const t = endpoint[labelend[bs] as number] as number;
        const bt = inblossom[t] as number;
        s = endpoint[labelend[bt] as number] as number;
        const j = endpoint[(labelend[bt] as number) ^ 1] as number;
        if (bt >= nvertex) augmentBlossom(bt, j);
        mate[j] = labelend[bt] as number;
        p = (labelend[bt] as number) ^ 1;
      }
    }
  };

  for (let stage = 0; stage < nvertex; stage++) {
    label.fill(0);
    bestedge.fill(-1);
    for (let b = nvertex; b < 2 * nvertex; b++) blossombestedges[b] = null;
    allowedge.fill(0);
    queue = [];
    for (let v = 0; v < nvertex; v++) {
      if (mate[v] === -1 && label[inblossom[v] as number] === 0) assignLabel(v, 1, -1);
    }
    let augmented = false;
    for (;;) {
      while (queue.length > 0 && !augmented) {
        const v = queue.pop() as number;
        for (const p of neighbend[v] as number[]) {
          const k = p >> 1;
          const w = endpoint[p] as number;
          if (inblossom[v] === inblossom[w]) continue;
          let kslack = 0n;
          if (!allowedge[k]) {
            kslack = slack(k);
            if (kslack <= 0n) allowedge[k] = 1;
          }
          if (allowedge[k]) {
            if (label[inblossom[w] as number] === 0) {
              assignLabel(w, 2, p ^ 1);
            } else if (label[inblossom[w] as number] === 1) {
              const base = scanBlossom(v, w);
              if (base >= 0) {
                addBlossom(base, k);
              } else {
                augmentMatching(k);
                augmented = true;
                break;
              }
            } else if (label[w] === 0) {
              label[w] = 2;
              labelend[w] = p ^ 1;
            }
          } else if (label[inblossom[w] as number] === 1) {
            const b = inblossom[v] as number;
            if (bestedge[b] === -1 || kslack < slack(bestedge[b] as number)) bestedge[b] = k;
          } else if (label[w] === 0) {
            if (bestedge[w] === -1 || kslack < slack(bestedge[w] as number)) bestedge[w] = k;
          }
        }
      }
      if (augmented) break;

      let deltatype = -1;
      let delta = 0n;
      let deltaedge = -1;
      let deltablossom = -1;
      if (!maxCardinality) {
        deltatype = 1;
        delta = dualvar[0] as bigint;
        for (let v = 1; v < nvertex; v++) {
          if ((dualvar[v] as bigint) < delta) delta = dualvar[v] as bigint;
        }
      }
      for (let v = 0; v < nvertex; v++) {
        if (label[inblossom[v] as number] === 0 && bestedge[v] !== -1) {
          const d = slack(bestedge[v] as number);
          if (deltatype === -1 || d < delta) {
            delta = d;
            deltatype = 2;
            deltaedge = bestedge[v] as number;
          }
        }
      }
      for (let b = 0; b < 2 * nvertex; b++) {
        if (blossomparent[b] === -1 && label[b] === 1 && bestedge[b] !== -1) {
          const d = slack(bestedge[b] as number) / 2n;
          if (deltatype === -1 || d < delta) {
            delta = d;
            deltatype = 3;
            deltaedge = bestedge[b] as number;
          }
        }
      }
      for (let b = nvertex; b < 2 * nvertex; b++) {
        if (
          (blossombase[b] as number) >= 0 &&
          blossomparent[b] === -1 &&
          label[b] === 2 &&
          (deltatype === -1 || (dualvar[b] as bigint) < delta)
        ) {
          delta = dualvar[b] as bigint;
          deltatype = 4;
          deltablossom = b;
        }
      }
      if (deltatype === -1) {
        // No further improvement possible; max-cardinality optimum reached.
        deltatype = 1;
        let m = dualvar[0] as bigint;
        for (let v = 1; v < nvertex; v++) if ((dualvar[v] as bigint) < m) m = dualvar[v] as bigint;
        delta = m > 0n ? m : 0n;
      }

      for (let v = 0; v < nvertex; v++) {
        const lb = label[inblossom[v] as number];
        if (lb === 1) dualvar[v] = (dualvar[v] as bigint) - delta;
        else if (lb === 2) dualvar[v] = (dualvar[v] as bigint) + delta;
      }
      for (let b = nvertex; b < 2 * nvertex; b++) {
        if ((blossombase[b] as number) >= 0 && blossomparent[b] === -1) {
          if (label[b] === 1) dualvar[b] = (dualvar[b] as bigint) + delta;
          else if (label[b] === 2) dualvar[b] = (dualvar[b] as bigint) - delta;
        }
      }

      if (deltatype === 1) {
        break;
      } else if (deltatype === 2) {
        allowedge[deltaedge] = 1;
        let i = eu[deltaedge] as number;
        const j = evv[deltaedge] as number;
        if (label[inblossom[i] as number] === 0) i = j;
        queue.push(i);
      } else if (deltatype === 3) {
        allowedge[deltaedge] = 1;
        queue.push(eu[deltaedge] as number);
      } else if (deltatype === 4) {
        expandBlossom(deltablossom, false);
      }
    }
    if (!augmented) break;
    for (let b = nvertex; b < 2 * nvertex; b++) {
      if (
        blossomparent[b] === -1 &&
        (blossombase[b] as number) >= 0 &&
        label[b] === 1 &&
        dualvar[b] === 0n
      ) {
        expandBlossom(b, true);
      }
    }
  }

  for (let v = 0; v < nvertex; v++) {
    const m = mate[v] as number;
    if (m >= 0) result[v] = endpoint[m] as number;
  }
  return result;
}

/** Total weight of a matching given as a mate array. */
export function matchingWeight(edges: readonly WeightedEdge[], mate: Int32Array): bigint {
  let total = 0n;
  for (const e of edges) if (mate[e.u] === e.v) total += e.w;
  return total;
}
